/* eslint-disable no-shadow */

/* eslint-disable react-hooks/rules-of-hooks */

/* eslint-disable react/destructuring-assignment */
import type { CalendarDate } from '@internationalized/date';
import { parseDate } from '@internationalized/date';
import {
  Button,
  DatePicker,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@nextui-org/react';
import Spacing from '@simppl/component/Spacing';
import Text from '@simppl/component/Text';
import useMutation from '@simppl/react-query/useMutation';
import useQueryClient from '@simppl/react-query/useQueryClient';
import useSuspenseQuery from '@simppl/react-query/useSuspenseQuery';
import { admissionSeasonApi } from '@simppl/repository-admin/admission';
import type { PatchAdmissionSeasonReqType } from '@simppl/repository-admin/admission/req';
import { extractDateFromTimestamp } from '@simppl/util/date';
import { ChevronDownIcon } from 'lucide-react';
import { useState } from 'react';

type ApplicantSeasonModalProps = {
  isOpen: boolean;
  onOpenChange: (value: boolean) => void;
  onClose: () => void;
};

enum ApplicantSeasons {
  '12특(후기)' = '12Y',
  '3특(전기)' = '3Y',
  '연세대 수시' = 'EARLY_YONSEI',
  '카이스트 수시' = 'EARLY_KAIST',
  '전형기간 아님' = 'DOWN_TIME',
}

const getSeasonKeyByValue = (value: ApplicantSeasons) => {
  return Object.entries(ApplicantSeasons).find(([key, val]) => val === value)?.[0];
};

export function AdmissionSeasonModal(props: ApplicantSeasonModalProps) {
  const {
    data: { season, nextAdmissionDate: nextAdmissionDateOnServer },
  } = useSuspenseQuery({
    queryKey: ['application-season'],
    queryFn: () => admissionSeasonApi.get(),
  });

  const [selectedKeys, setSelectedKeys] = useState<Set<ApplicantSeasons>>(
    new Set([(season as ApplicantSeasons) || ApplicantSeasons['12특(후기)']]),
  );

  const selectedSeason = [...selectedKeys][0];

  const [nextAdmissionDate, setNextAdmissionDate] = useState<string>('');
  const onNextAdmissionDateChange = (value: string) => {
    setNextAdmissionDate(value);
  };

  const queryClient = useQueryClient();

  const patchApplicantSeasonHook = useMutation(admissionSeasonApi.patch);

  return (
    <Modal
      isOpen={props.isOpen}
      onOpenChange={props.onOpenChange}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className='flex flex-col gap-1'>모집 전형 관리</ModalHeader>
            <ModalBody>
              <Text
                size={1}
                as='Title'
                color='text-Blue-5'
                weight='Bold'
              >
                현재 모집전형: {getSeasonKeyByValue(season as ApplicantSeasons)}
              </Text>
              <Spacing height={24} />
              <Text
                size={2}
                as='Label'
                color='text-Blue-1'
                weight='Medium'
              >
                - 3특 / 12특 선택 시: 성적 입력 [ 고교 / 어학 / 표준화 / 활동수상] <br />
                <br />- 3특 선택 시: 12특 + 3특 동시 오픈
                <br />
                <br />- 연세대 / 카이스트 선택 시: 성적 입력 [ 고교 / 표준화 ]
              </Text>
              <Spacing height={24} />
              <Text
                size={1}
                as='Body'
                color='text-Blue-5'
                weight='Bold'
              >
                모집전형 변경
              </Text>
              <Dropdown>
                <DropdownTrigger className='hidden sm:flex'>
                  <Button endContent={<ChevronDownIcon className='text-small' />}>
                    {getSeasonKeyByValue([...selectedKeys][0]) || '선택'}
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  disallowEmptySelection
                  aria-label='Table Columns'
                  closeOnSelect
                  selectionMode='single'
                  selectedKeys={selectedKeys}
                  onSelectionChange={(keys) => {
                    const newSet = new Set(
                      Array.from(keys).map(
                        (key) => ApplicantSeasons[key as keyof typeof ApplicantSeasons],
                      ),
                    );
                    setSelectedKeys(newSet);
                  }}
                >
                  {Object.entries(ApplicantSeasons).map(([key, value]) => (
                    <DropdownItem
                      key={key}
                      className='capitalize'
                    >
                      {key}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
              {selectedSeason === ApplicantSeasons['전형기간 아님'] && (
                <NextAdmissionDate
                  nextAdmissionDateOnServer={nextAdmissionDateOnServer}
                  nextAdmissionDate={nextAdmissionDate}
                  onNextAdmissionDateChange={setNextAdmissionDate}
                />
              )}
            </ModalBody>
            <Spacing height={24} />
            <ModalFooter>
              <Button
                color='danger'
                variant='light'
                onPress={props.onClose}
              >
                취소
              </Button>
              <Button
                type='submit'
                form='authorization'
                color='primary'
                onPress={() => {
                  let data: PatchAdmissionSeasonReqType = { season: [...selectedKeys][0] };
                  if (data.season === ApplicantSeasons['전형기간 아님']) {
                    data = { ...data, nextAdmissionDate };
                  }

                  patchApplicantSeasonHook.mutate(data, {
                    onSuccess: () => {
                      queryClient.invalidateQueries(['application-season']);
                      props.onClose();
                    },
                  });
                }}
              >
                변경
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

const NextAdmissionDate = ({
  nextAdmissionDateOnServer,
  nextAdmissionDate,
  onNextAdmissionDateChange,
}: {
  nextAdmissionDateOnServer?: string;
  nextAdmissionDate: string;
  onNextAdmissionDateChange: (value: string) => void;
}) => (
  <>
    <Spacing height={24} />
    <Text
      size={1}
      as='Body'
      color='text-Blue-1'
      weight='Bold'
    >
      다음 모집 전형 시작일 설정 (모의지원 ALERT 용)
    </Text>
    <Text
      size={2}
      as='Label'
      color='text-Blue-5'
      weight='Bold'
    >
      현재 설정된 다음 전형 시작일: {nextAdmissionDateOnServer || '없음'}
    </Text>
    <Input
      value={nextAdmissionDate}
      onChange={(e) => onNextAdmissionDateChange(e.target.value)}
    />
  </>
);
